import { useAppDispatch, useAppSelector } from "app/hooks"
import {
  createSchedule,
  deleteSchedule,
  getScheduleDetails,
  getSchedules,
  updateSchedule,
  updateScheduleEndDate,
} from "../scheduleSlice"
import { useFailed, useSuccess } from "features/notification/hooks"
import { useEffect } from "react"
import { useParams } from "react-router-dom"
import {
  ICreateSchedulePayload,
  IUpdateSchedulePayload,
} from "model/food/types/schedulePayloads.types"
import { FoodCreationMethod } from "model/food/types/mealPayload.types"

export const useCreateSchedule = (callback?: () => void) => {
  const createScheduleLoading = useAppSelector(
    (state) => state.schedule.createScheduleLoading,
  )
  const createScheduleSuccess = useAppSelector(
    (state) => state.schedule.createScheduleSuccess,
  )
  const createScheduleFailed = useAppSelector(
    (state) => state.schedule.createScheduleFailed,
  )

  const dispatch = useAppDispatch()

  const handleCreateSchedule = (
    payload: ICreateSchedulePayload,
    foodCreationMethod?: FoodCreationMethod,
  ) => {
    if (foodCreationMethod) {
      payload.creationMethod = foodCreationMethod
    }
    
    dispatch(createSchedule(payload))
  }

  useFailed(createScheduleFailed)

  useSuccess(createScheduleSuccess, { message: "" }, callback)

  return {
    createScheduleLoading,
    createScheduleSuccess,
    createScheduleFailed,
    handleCreateSchedule,
  }
}

export const useSchedules = () => {
  const schedules = useAppSelector((state) => state.schedule.schedules)
  const getSchedulesLoading = useAppSelector(
    (state) => state.schedule.getSchedulesLoading,
  )
  const getSchedulesFailed = useAppSelector(
    (state) => state.schedule.getSchedulesFailed,
  )

  const isNoSchedules = useAppSelector((state) => state.schedule.isNoSchedules)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getSchedules())
  }, [])

  useFailed(getSchedulesFailed)

  return {
    schedules,
    getSchedulesLoading,
    isNoSchedules,
  }
}

export const useScheduleDetails = () => {
  const scheduleDetails = useAppSelector(
    (state) => state.schedule.scheduleDetails,
  )
  const getScheduleDetailsLoading = useAppSelector(
    (state) => state.schedule.getScheduleDetailsLoading,
  )
  const getScheduleDetailsFailed = useAppSelector(
    (state) => state.schedule.getScheduleDetailsFailed,
  )

  const dispatch = useAppDispatch()
  const { id } = useParams()

  useEffect(() => {
    if (id) {
      dispatch(getScheduleDetails(id))
    }
  }, [])

  useFailed(getScheduleDetailsFailed)

  return {
    scheduleDetails,
    getScheduleDetailsLoading,
    getScheduleDetailsFailed,
  }
}

export const useUpdateSchedule = (callback?: () => void) => {
  const updateScheduleLoading = useAppSelector(
    (state) => state.schedule.updateScheduleLoading,
  )

  const updateScheduleSuccess = useAppSelector(
    (state) => state.schedule.updateScheduleSuccess,
  )

  const updateScheduleFailed = useAppSelector(
    (state) => state.schedule.updateScheduleFailed,
  )

  const dispatch = useAppDispatch()

  const handleUpdateSchedule = (payload: IUpdateSchedulePayload) => {
    dispatch(updateSchedule(payload))
  }

  useFailed(updateScheduleFailed)
  useSuccess(updateScheduleSuccess, { message: "" }, callback)

  return {
    updateScheduleLoading,
    updateScheduleSuccess,
    updateScheduleFailed,
    handleUpdateSchedule,
  }
}

export const useDeleteSchedule = (callback?: () => void) => {
  const deleteScheduleLoading = useAppSelector(
    (state) => state.schedule.deleteScheduleLoading,
  )

  const deleteScheduleSuccess = useAppSelector(
    (state) => state.schedule.deleteScheduleSuccess,
  )

  const deleteScheduleFailed = useAppSelector(
    (state) => state.schedule.deleteScheduleFailed,
  )

  const dispatch = useAppDispatch()

  const handleDeleteSchedule = (id: string) => {
    dispatch(deleteSchedule(id))
  }

  useFailed(deleteScheduleFailed)
  useSuccess(deleteScheduleSuccess, { message: "" }, callback)

  return {
    deleteScheduleLoading,
    deleteScheduleSuccess,
    deleteScheduleFailed,
    handleDeleteSchedule,
  }
}

export const useUpdateScheduleEndDate = (callback?: () => void) => {
  const updateScheduleEndDateLoading = useAppSelector(
    (state) => state.schedule.updateScheduleEndDateLoading,
  )

  const updateScheduleEndDateSuccess = useAppSelector(
    (state) => state.schedule.updateScheduleEndDateSuccess,
  )

  const updateScheduleEndDateFailed = useAppSelector(
    (state) => state.schedule.updateScheduleEndDateFailed,
  )

  const dispatch = useAppDispatch()

  const handleUpdateScheduleEndDate = (payload: {
    scheduleId: string
    fromDate: string
  }) => {
    dispatch(updateScheduleEndDate(payload))
  }

  useFailed(updateScheduleEndDateFailed)
  useSuccess(updateScheduleEndDateSuccess, { message: "" }, callback)

  return {
    updateScheduleEndDateLoading,
    updateScheduleEndDateSuccess,
    updateScheduleEndDateFailed,
    handleUpdateScheduleEndDate,
  }
}
